import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { ArrowUpRight, CheckCircle2, Loader2 } from "lucide-react";
import { Eyebrow, MaskLines } from "./Reveal";
import { CONTACT } from "./data";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const EMPTY = { full_name: "", email: "", phone: "", project_type: "residential", estimated_budget: "", project_details: "" };
const BUDGETS = ["Under $100k", "$100k – $500k", "$500k – $1M", "$1M+", "Not sure yet"];

const Field = ({ label, children }) => (
  <label className="block">
    <span className="mb-2 block font-mono text-[11px] uppercase tracking-[0.2em] text-slate-300">{label}</span>
    {children}
  </label>
);

export const Contact = () => {
  const [form, setForm] = useState(EMPTY);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/quotes`, form);
      setDone(true);
      setForm(EMPTY);
      toast.success("Quote request sent. We'll be in touch shortly.");
    } catch (err) {
      toast.error("Could not send request. Please check your details and try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="contact" data-testid="contact-section" className="bg-[#0D1015] py-24 sm:py-32">
      <div className="mx-auto grid max-w-7xl gap-14 px-5 sm:px-8 lg:grid-cols-12 lg:px-12">
        <div className="lg:col-span-5">
          <Eyebrow>Start a project</Eyebrow>
          <h2 className="font-display text-5xl font-black uppercase leading-[0.9] text-white sm:text-6xl lg:text-7xl">
            <MaskLines inView lines={["Let's talk", <span key="b" className="text-blue-500">your build.</span>]} />
          </h2>
          <p className="mt-8 max-w-md text-base leading-relaxed text-slate-300 sm:text-lg">Tell us about the project. We'll review it and get back to you with next steps and a ballpark.</p>
          <dl className="mt-10 space-y-5 font-mono text-sm">
            {[
              ["Business", "Bennett Custom Builders"],
              ["Phone", <a href={CONTACT.phoneHref} className="nav-link hover:text-blue-400">{CONTACT.phone} (Mobile)</a>],
              ["Email", <a href={`mailto:${CONTACT.email}`} className="nav-link hover:text-blue-400">{CONTACT.email}</a>],
              ["Area", CONTACT.area],
              ["License", CONTACT.license],
            ].map(([k, v]) => (
              <div key={k} className="flex flex-col gap-2 border-t border-white/10 pt-5 sm:flex-row sm:gap-6">
                <dt className="w-20 shrink-0 uppercase tracking-[0.2em] text-blue-400">{k}</dt>
                <dd data-testid={`contact-${k.toLowerCase()}`} className="text-white">{v}</dd>
              </div>
            ))}
          </dl>
        </div>
        <div className="lg:col-span-7">
          {done ? (
            <motion.div initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }} data-testid="quote-form-success-message" className="clip-corner flex min-h-[420px] flex-col items-start justify-center border border-blue-500/50 bg-[#12161F] p-10">
              <CheckCircle2 className="h-12 w-12 text-blue-500" />
              <h3 className="mt-6 font-display text-4xl font-black uppercase text-white">Request received.</h3>
              <p className="mt-3 text-slate-300">Thanks — a member of our team will reach out soon.</p>
              <button data-testid="quote-form-new-request" onClick={() => setDone(false)} className="btn-ghost mt-8">Send another</button>
            </motion.div>
          ) : (
            <form onSubmit={submit} data-testid="quote-form" className="clip-corner grid gap-6 border border-white/10 bg-[#12161F] p-6 sm:grid-cols-2 sm:p-10">
              <Field label="Full name *"><input required minLength={2} value={form.full_name} onChange={set("full_name")} data-testid="quote-form-name-input" className="field" placeholder="Jane Smith" /></Field>
              <Field label="Email *"><input required type="email" value={form.email} onChange={set("email")} data-testid="quote-form-email-input" className="field" placeholder="jane@email.com" /></Field>
              <Field label="Phone"><input value={form.phone} onChange={set("phone")} data-testid="quote-form-phone-input" className="field" placeholder="(555) 000-0000" /></Field>
              <Field label="Budget">
                <select value={form.estimated_budget} onChange={set("estimated_budget")} data-testid="quote-form-budget-select" className="field">
                  <option value="">Select range</option>
                  {BUDGETS.map((b) => <option key={b}>{b}</option>)}
                </select>
              </Field>
              <div className="sm:col-span-2">
                <span className="mb-2 block font-mono text-[11px] uppercase tracking-[0.2em] text-slate-300">Project type *</span>
                <div data-testid="quote-form-project-type-select" className="grid grid-cols-1 gap-2 sm:grid-cols-3">
                  {["residential", "commercial", "renovation"].map((t) => (
                    <button type="button" key={t} data-testid={`project-type-${t}`} onClick={() => setForm({ ...form, project_type: t })} className={`border px-4 py-3 font-mono text-xs uppercase tracking-[0.2em] transition-colors ${form.project_type === t ? "border-blue-600 bg-blue-700 text-white" : "border-white/15 text-slate-300 hover:border-white/40"}`}>{t}</button>
                  ))}
                </div>
              </div>
              <div className="sm:col-span-2">
                <Field label="Project details"><textarea rows={4} value={form.project_details} onChange={set("project_details")} data-testid="quote-form-notes-input" className="field resize-none" placeholder="Location, size, timeline, anything we should know…" data-lenis-prevent /></Field>
              </div>
              <button type="submit" disabled={loading} data-testid="quote-form-submit-button" className="btn-primary justify-center sm:col-span-2 disabled:opacity-60">
                {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <>Send Request <ArrowUpRight className="h-4 w-4" /></>}
              </button>
            </form>
          )}
        </div>
      </div>
    </section>
  );
};
